import type { VolunteerRoleAttributes, VolunteerTeamAttributes } from '@/server/models'

import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import UserVolunteerRoleQ from './UserVolunteerRoleQ'
import VolunteerTeamQ from './VolunteerTeamQ'

const VolunteerRoleQ = {
  ...makeQueryModelV2<VolunteerRoleAttributes>('volunteer_role')([
    'id',
    'slug',
    'name',
    'type',
    'volunteer_team_id',
    'description',
    'qualifications',
    'responsibilities',
    'application_opened_at',
    'application_form_blocks',
    'archived_at',
    'created_at',
    'updated_at',
    'deleted_at',
    'requirements',
    'omit_general_questions',
    'offer_message',
  ]),

  associations: (): AssociationDefinitions => ({
    VolunteerTeam: {
      query: VolunteerTeamQ,
      foreignKey: VolunteerTeamQ.id,
      sourceKey: VolunteerRoleQ.volunteer_team_id,
    },
    UserVolunteerRoles: {
      query: UserVolunteerRoleQ,
      foreignKey: UserVolunteerRoleQ.volunteer_role_id,
      sourceKey: VolunteerRoleQ.id,
    },
  }),

  where: (
    {
      id,
      volunteerTeamId,
      slug,
      include = [],
      paranoid = true,
      groupBy = null,
      order = null,
    }: {
      id?: number | null | SQLFragment<number>
      volunteerTeamId?: number | null | SQLFragment<number>
      slug?: string | SQLFragment<string>
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<VolunteerRoleAttributes> => SQL`
        FROM ${VolunteerRoleQ}
        ${includeAssociationsToSql(VolunteerRoleQ, include)}
        WHERE
          ${id != null ? SQL`${VolunteerRoleQ.id} = ${id} AND` : SQL``}
          ${
            volunteerTeamId != null
              ? SQL`${VolunteerRoleQ.volunteer_team_id} = ${volunteerTeamId} AND`
              : SQL``
          }
          ${slug != null ? SQL`${VolunteerRoleQ.slug} = ${slug} AND` : SQL``}
          ${paranoid ? SQL`${VolunteerRoleQ.deleted_at} IS NULL AND` : SQL``}
          ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  isApplicationOpen: (): SQLFragment<boolean> =>
    SQL`${VolunteerRoleQ.application_opened_at} < NOW()`,

  getTeam: (): SQLFragment<VolunteerTeamAttributes> =>
    VolunteerTeamQ.where({ id: VolunteerRoleQ.volunteer_team_id }),
}

export default VolunteerRoleQ
